import { Add, Delete, ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, IconButton } from '@mui/material';
import React, { useContext, useState } from 'react';
import { DisableContext } from '../../contexts/DisableContextProvider';
import { useCreateAttachments, useDestroyAttachments, useGetAttachmentsByOwner } from '../../services/react-query/attachments';
import { Attachment, AttachmentOwnerType } from '../../types/Attachment';
import { Loading } from '../../uikit';
import ResponsiveButton from '../../uikit/buttons/ResponsiveButton';
import UploadModal from '../../components/attachments/UploadModal';
import ConfirmationModal from '../../components/ConfirmationModal';
import { formatSize, getFileTypeIcon } from '../../utility/Attachment';
import { humanizeTimestamp } from '../../utility/dateUtils';
import _ from 'lodash';

interface IFormSubmissionAttachmentsProps {
  ownerType: AttachmentOwnerType;
  ownerUuid: string;
}

export default function FormSubmissionAttachments({ ownerType, ownerUuid }: IFormSubmissionAttachmentsProps) {
  const [uploadOpen, setUploadOpen] = useState<boolean>(false);
  const [keyToDestroy, setKeyToDestroy] = useState<string | null>(null);
  const { data: attachmentData } = useGetAttachmentsByOwner({ type: ownerType, uuid: ownerUuid });
  const { disabled } = useContext(DisableContext);
  const { mutate: uploadFiles } = useCreateAttachments({
    type: ownerType,
    uuid: ownerUuid,
    onSuccessCallback: () => setUploadOpen(false),
  });
  const { mutate: destroyFiles } = useDestroyAttachments({
    type: ownerType,
    uuid: ownerUuid,
    onSuccessCallback: () => setKeyToDestroy(null),
  });
  const isEditable = ownerType == AttachmentOwnerType.FORM_SUBMISSION;

  if (!attachmentData) return <Loading />;

  const renderRow = (attachment: Attachment) => {
    const { key, name, url, content_type, date, size } = attachment;
    return (
      <TableRow key={key}>
        <TableCell>
          <Box>
            <Link color="inherit" href={url} target="blank" sx={{ display: 'flex', alignItems: 'center' }}>
              {getFileTypeIcon(content_type)}
              {name}
            </Link>
          </Box>
        </TableCell>
        <TableCell>{humanizeTimestamp(date)}</TableCell>
        <TableCell>{formatSize(size)}</TableCell>
        {
          isEditable &&
          <TableCell>
            <IconButton onClick={() => setKeyToDestroy(key)}>
              <Delete />
            </IconButton>
          </TableCell>
        }
      </TableRow>
    );
  };

  return (
    <Accordion defaultExpanded={true} sx={{ height: 'fit-content', marginBottom: 2, marginTop: 0 }}>
      <AccordionSummary
        component={Box}
        expandIcon={<ExpandMore />}
        sx={{ borderColor: '#E3E3E5' }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', mr: 1 }}>
          <Typography variant="h6" fontWeight="bold" lineHeight="1.5">
            Files to {isEditable ? 'upload' : 'download'}
          </Typography>

          {
            isEditable &&
            <ResponsiveButton disabled={disabled} startIcon={<Add />} variant="outlined" onClick={(e) => { e.stopPropagation(); setUploadOpen(true); }}>
              Add
            </ResponsiveButton>
          }
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: { xs: 1, sm: 2 } }}>
        <Box sx={{ marginBottom: { xs: 2, lg: 0 } }}>
          {
            attachmentData.length == 0 ?
              <Typography align='center' variant="h6">No files to show</Typography> :
              <TableContainer sx={{ width: '100%' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>FILE NAME</TableCell>
                      <TableCell>DATE ADDED</TableCell>
                      <TableCell>SIZE</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {_.map(attachmentData, renderRow)}
                  </TableBody>
                </Table>
              </TableContainer>
          }

          <UploadModal isOpen={uploadOpen} onCancel={() => setUploadOpen(false)} onUpload={uploadFiles} />
          <ConfirmationModal
            text="Delete this file?"
            isOpen={keyToDestroy !== null}
            onConfirm={() => keyToDestroy && destroyFiles([keyToDestroy])}
            onCancel={() => setKeyToDestroy(null)}
          />
        </Box >
      </AccordionDetails>
    </Accordion>
  );
}
